import { Controller } from "@hotwired/stimulus";

/**
 * Turbo Frame Refresh Controller
 *
 * This controller provides a way to refresh a Turbo Frame at regular intervals.
 * It's useful for polling operations where you need to see updates in real-time.
 *
 * Usage:
 * <%= turbo_frame_tag "my_frame",
 *                    src: my_path,
 *                    data: { controller: "turbo-frame-refresh",
 *                           turbo_frame_refresh_interval_value: 2000 } %>
 */
export default class extends Controller {
  static values = {
    interval: { type: Number, default: 2000 },
    active: { type: Boolean, default: true },
    // Opt-in: stop polling while the tab is hidden, and catch up on return.
    // Off by default so short-lived pollers (import progress) keep their
    // existing behavior; long-lived ones (a public dashboard left open for
    // days) would otherwise poll a tab nobody is looking at.
    pauseWhenHidden: { type: Boolean, default: false },
  };

  connect() {
    // Check if the element is a turbo-frame
    if (this.element.tagName !== "TURBO-FRAME") {
      console.warn(
        "Turbo frame refresh controller must be attached to a turbo-frame element",
      );
      return;
    }

    if (this.pauseWhenHiddenValue) {
      this.visibilityListener = () => this.handleVisibilityChange();
      document.addEventListener("visibilitychange", this.visibilityListener);
      if (document.hidden) return;
    }

    this.startRefreshing();
  }

  disconnect() {
    this.stopRefreshing();
    if (this.visibilityListener) {
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.visibilityListener = null;
    }
  }

  // Coming back to a tab that has been hidden for a while should show current
  // numbers straight away, not whatever was on screen when it was left.
  handleVisibilityChange() {
    if (document.hidden) {
      this.stopRefreshing();
      return;
    }

    this.refreshFrame();
    this.startRefreshing();
  }

  startRefreshing() {
    this.stopRefreshing();

    if (this.activeValue) {
      this.refreshTimer = setInterval(() => {
        this.refreshFrame();
      }, this.intervalValue);
    }
  }

  stopRefreshing() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  refreshFrame() {
    if (this.element && this.element.src) {
      // Force a refresh by appending a timestamp to the URL
      const url = new URL(this.element.src, window.location.origin);
      url.searchParams.set("_", Date.now());
      this.element.src = url.toString();
    }
  }

  // This can be called from outside to pause/resume refreshing
  toggleActive() {
    this.activeValue = !this.activeValue;

    if (this.activeValue) {
      this.startRefreshing();
    } else {
      this.stopRefreshing();
    }
  }
}
